import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Briefcase, Clock, ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { careers as mockCareers } from '@/data/mock';

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fallbackJob = mockCareers.find((career) => String(career.id) === String(jobId));

    if (!backendUrl) {
      setJob(fallbackJob || null);
      setLoading(false);
      return;
    }

    const fetchJob = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/careers/${jobId}`);
        setJob(response.data);
      } catch (error) {
        console.error('Failed to fetch job:', error);
        setJob(fallbackJob || null);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [jobId, backendUrl]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  if (!job) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h1>
            <Link to="/careers">
              <Button variant="outline">Back to Careers</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead
        title={`${job.title} - Careers | Redspire Civil Engineering`}
        description={job.description}
        keywords={['civil engineering jobs', job.title, job.department, 'careers']}
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/careers">
            <Button variant="ghost" className="gap-2 text-white hover:text-white hover:bg-white/10 mb-6">
              <ArrowLeft className="w-4 h-4" />
              Back to Careers
            </Button>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{job.title}</h1>
          <div className="flex flex-wrap gap-4 text-gray-300">
            <span className="flex items-center gap-2"><MapPin className="w-5 h-5" /> {job.location}</span>
            <span className="flex items-center gap-2"><Briefcase className="w-5 h-5" /> {job.type}</span>
            <span className="flex items-center gap-2"><Clock className="w-5 h-5" /> {job.department}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Job Description</h2>
                <p className="text-gray-700 leading-relaxed">{job.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Responsibilities</h2>
                <ul className="space-y-2">
                  {job.responsibilities.map((resp, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span className="text-gray-700">{resp}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Qualifications</h2>
                <ul className="space-y-2">
                  {job.qualifications.map((qual, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">•</span>
                      <span className="text-gray-700">{qual}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {job.benefits && job.benefits.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Benefits</h2>
                  <ul className="space-y-2">
                    {job.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-600 mt-1">•</span>
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Apply for this position</h3>
                  <Button
                    onClick={() => navigate(`/careers/${jobId}/apply`)}
                    className="w-full bg-red-600 hover:bg-red-700 gap-2"
                    size="lg"
                  >
                    Apply Now
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="pt-6 border-t border-gray-200 space-y-3 text-sm">
                  <div>
                    <p className="text-gray-600 font-medium">Department</p>
                    <p className="text-gray-900">{job.department}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Location</p>
                    <p className="text-gray-900">{job.location}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Employment Type</p>
                    <p className="text-gray-900">{job.type}</p>
                  </div>
                  <div>
                    <p className="text-gray-600 font-medium">Posted Date</p>
                <p className="text-gray-900">{job.postedDate ? new Date(job.postedDate).toLocaleDateString() : 'Rolling applications'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default JobDetailPage;
